import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Card, CardTitle, cn } from "@clevis/shared-ui";
import { call } from "../lib/callFunction";
import { downloadCsv } from "../lib/csv";

// The week's piece-rate pay: per tech, every piece-rate job they finished,
// what each earned and why (parts installed, the project item, the job type's
// flat rate). Managers see everyone; a tech sees their own week.

interface Line {
  source: "part" | "item" | "type";
  description: string;
  quantity: number;
  rateCents: number;
  amountCents: number;
}
interface JobRow {
  jobId: string;
  title: string;
  customerName: string;
  lot: string;
  doneAt: number;
  lines: Line[];
  jobTotalCents: number;
  shareCents: number;
  techCount: number;
}
interface TechRow {
  uid: string;
  name: string;
  totalCents: number;
  jobs: JobRow[];
}

const money = (c: number) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
const DAY = 86400000;
/** Monday 00:00 of the week containing `ms`. */
function weekStart(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  const back = (d.getDay() + 6) % 7;
  return d.getTime() - back * DAY;
}

export function PieceRateReport({ tenantId, mine = false, people }: { tenantId: string; mine?: boolean; /** Only these techs (a department). */ people?: (uid: string) => boolean }) {
  const [from, setFrom] = useState(() => weekStart(Date.now()));
  const [data, setData] = useState<{ techs: TechRow[]; split: string } | null>(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const to = from + 7 * DAY;

  useEffect(() => {
    setData(null);
    setError("");
    void call<{ techs: TechRow[]; split: string }>("getPieceRateReport", { tenantId, from, to }, { mutation: false })
      .then(setData)
      .catch((err) => setError((err as Error).message));
  }, [tenantId, from, to]);

  const label = `${new Date(from).toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${new Date(to - DAY).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  const techsShown = data ? data.techs.filter((t) => !people || people(t.uid)) : [];
  const total = techsShown.reduce((s, t) => s + t.totalCents, 0);

  function exportCsv() {
    if (!data) return;
    downloadCsv(
      `piece-pay-${new Date(from).toISOString().slice(0, 10)}`,
      ["Tech", "Job", "Customer", "Lot", "Finished", "Pieces", "Job total", "Techs on job", "Tech's share"],
      techsShown.flatMap((t) =>
        t.jobs.map((j) => [
          t.name,
          j.title,
          j.customerName,
          j.lot,
          new Date(j.doneAt).toLocaleDateString(),
          j.lines.map((l) => `${l.quantity} × ${l.description} @ ${money(l.rateCents)}`).join("; "),
          (j.jobTotalCents / 100).toFixed(2),
          j.techCount,
          (j.shareCents / 100).toFixed(2),
        ]),
      ),
    );
  }

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <CardTitle className="mb-0">{mine ? "My piece pay" : "Piece-rate pay"}</CardTitle>
        <div className="flex items-center gap-1 text-sm">
          <button className="rounded px-2 text-steel hover:bg-cream hover:text-ink" aria-label="Previous week" onClick={() => setFrom((f) => f - 7 * DAY)}>
            ‹
          </button>
          <span className="min-w-32 text-center text-ink">{label}</span>
          <button className="rounded px-2 text-steel hover:bg-cream hover:text-ink" aria-label="Next week" onClick={() => setFrom((f) => f + 7 * DAY)}>
            ›
          </button>
          {!mine && data && techsShown.length > 0 ? (
            <button className="ml-2 text-xs text-steel hover:text-ink" onClick={exportCsv}>
              Export CSV
            </button>
          ) : null}
        </div>
      </div>
      {error ? <p className="mt-2 text-xs text-danger">{error}</p> : null}
      {!data && !error ? <p className="mt-2 text-xs text-steel">Adding up the week…</p> : null}
      {data ? (
        <>
          <p className="mt-1 text-xs text-steel">
            {techsShown.length === 0
              ? "No piece-rate jobs finished this week."
              : `${money(total)} across ${techsShown.length} tech${techsShown.length === 1 ? "" : "s"}${data.split === "each" ? " · each tech earns a job's pieces in full" : " · techs on the same job split it"}`}
          </p>
          <div className="mt-2 divide-y divide-steel/10">
            {techsShown.map((t) => (
              <div key={t.uid} className="py-1.5">
                <button className="flex w-full items-center justify-between text-left" onClick={() => setOpen(open === t.uid ? null : t.uid)}>
                  <span className="text-sm font-semibold text-ink">{mine ? "This week" : t.name}</span>
                  <span className="text-sm text-ink">
                    {money(t.totalCents)} <span className="text-xs text-steel">· {t.jobs.length} job{t.jobs.length === 1 ? "" : "s"} {open === t.uid || mine ? "▾" : "▸"}</span>
                  </span>
                </button>
                {open === t.uid || mine ? (
                  <div className="mt-1 space-y-1">
                    {t.jobs.map((j) => (
                      <div key={j.jobId} className="rounded-lg bg-cream/50 px-2 py-1 text-xs">
                        <div className="flex justify-between gap-2">
                          <Link href={`/jobs/${j.jobId}`} className="min-w-0 truncate text-brandorange-dark hover:underline">
                            {j.customerName ? `${j.customerName} · ` : ""}
                            {j.lot ? `${j.lot} · ` : ""}
                            {j.title}
                          </Link>
                          <span className="shrink-0 font-semibold text-ink">
                            {money(j.shareCents)}
                            {j.techCount > 1 ? <span className="font-normal text-steel"> of {money(j.jobTotalCents)}</span> : null}
                          </span>
                        </div>
                        {j.lines.map((l, i) => (
                          <div key={i} className={cn("flex justify-between text-steel")}>
                            <span>
                              {l.quantity} × {l.description} @ {money(l.rateCents)}
                            </span>
                            <span>{money(l.amountCents)}</span>
                          </div>
                        ))}
                        {j.lines.length === 0 ? <div className="text-steel">No piece rates set for what was done on this job.</div> : null}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </>
      ) : null}
    </Card>
  );
}
